import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import TableImage from "../../shared/Table/TableImage";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { getPlans, purchasePlan } from "../../redux/actions/planAction";
import { useDispatch, useSelector } from "react-redux";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { hasPermission } from "../../utils/adminPermissions";
import { exportTableToCsv } from "../../utils/exportTableCsv";

const sortByLinkedVehicles = (rowData) =>
    Array.isArray(rowData?.vehicles)
        ? rowData.vehicles.map((vehicle) => vehicle?.vehicleNumber).filter(Boolean).join(", ")
        : "";

const statusOptions = [
    { label: "All", value: "ALL" },
    { label: "Active", value: "ACTIVE" },
    { label: "Deactivated", value: "DEACTIVATED" },
];

// An account deactivated by an admin can only be restored from here; one the user
// deactivated themselves also comes back on their next login.
const accountStatus = (rowData) => {
    if (!rowData?.isDeleted) return { label: "Active", severity: "success" };
    return rowData?.deletedBy === "ADMIN"
        ? { label: "Deactivated by admin", severity: "danger" }
        : { label: "Deactivated by user", severity: "warning" };
};

export default function UserTable({ heading, allUsers, loading, setLoading, history, search, setSearch, status, setStatus, deleteUser, softDeleteUser, restoreUser, first, rows, onPage }) {
    const dispatch = useDispatch();
    let allPlans = useSelector((state) => state.plans.allPlans);
    const { userProfile } = useSelector((state) => state.profile);
    const canDeleteUsers = hasPermission(userProfile, "users.delete");
 
    const [selectedRow, setSelectedRow] = useState([]);
    const [visible, setVisible] = useState(false);
    const [dialogType, setDialogType] = useState("");
 
    // Plan handling
    const [planType, setPlanType] = useState(null);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(null);
 
    // Plan types
    const planTypes = [
        { label: "iOS", value: "IOS" },
        { label: "Android", value: "ANDROID" },
    ];
    useEffect(() => {
        if (planType) {
            dispatch(getPlans(planType));
        }
    }, [planType, dispatch]);
 
    const handleOpenPlanDialog = (rowData) => {
        setSelectedRow([rowData]);
        setDialogType("plan");
        setPlanType(null);
        setSelectedPlan(null);
        setStartDate(new Date());
        setEndDate(null);
        setVisible(true);
    };
 
    const handleSavePlan = () => {
        if (!planType || !selectedPlan) {
            alert("Please fill all fields");
            return;
        }
        const payload = {
            vehicleId: selectedRow?.[0]?.Vehicle?._id,
            userId: selectedRow?.[0]?._id,
            planType,
            planId: selectedPlan._id,
            // startDate,
            // endDate,
        };
        console.log("payload-->", payload);
 
        setVisible(false);
        dispatch(purchasePlan(payload, setLoading));
    };
    const handleSoftDeleteUser = () => {
        setVisible(false);
        softDeleteUser(selectedRow?.[0]?._id);
    };

    const handlePermanentDeleteUser = () => {
        setVisible(false);
        deleteUser(selectedRow?.[0]?._id);
    };

    const handleRestoreUser = () => {
        setVisible(false);
        restoreUser(selectedRow?.[0]?._id);
    };
    const actions = (d) => {
        console.log("d-->", d);
 
        const hasVehicle = d?.Vehicle?._id;
        return (
            <div className="flex justify-content-around">
                <i
                    className={`pi pi-plus cursor-pointer text-dark ${!hasVehicle ? "opacity-50 cursor-not-allowed" : ""}`}
                    onClick={() => hasVehicle && handleOpenPlanDialog(d)}
                    title={hasVehicle ? "Assign Plan" : "No Vehicle Found"}
                ></i>
                <i className="pi pi-eye cursor-pointer text-dark" onClick={() => history(`/user/view/${d._id}`)}></i>
                {canDeleteUsers && !d?.isDeleted && <i
                    className="pi pi-trash cursor-pointer text-dark"
                    title="Delete user"
                    onClick={() => {
                        setSelectedRow([d]);
                        setDialogType("delete");
                        setVisible(true);
                    }}
                ></i>}
                {canDeleteUsers && d?.isDeleted && <i
                    className="pi pi-replay cursor-pointer text-dark"
                    title="Restore account"
                    onClick={() => {
                        setSelectedRow([d]);
                        setDialogType("restore");
                        setVisible(true);
                    }}
                ></i>}
            </div>
        );
    };
 
    const image = (r) => {
        const image = r.image;
        return <TableImage image={image} />;
    };
    const statusBody = (rowData) => {
        const { label, severity } = accountStatus(rowData);
        return <Tag value={label} severity={severity} />;
    };
    const vehicles = (rowData) => {
        const linkedVehicles = Array.isArray(rowData?.vehicles) ? rowData.vehicles : [];
        return linkedVehicles.length
            ? linkedVehicles.map((vehicle) => vehicle?.vehicleNumber).filter(Boolean).join(", ")
            : "-";
    };
    const dateFilterTemplate = (options) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => {
                    options.filterCallback(e.value, options.index);
                }}
                dateFormat="mm/dd/yy"
                placeholder="mm/dd/yyyy"
                mask="99/99/9999"
                maxDate={new Date()}
            />
        );
    };
    const filters = {
        time: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    };
    const formatDate = (date) => {
        return date.toLocaleDateString("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const exportAllUsers = () => {
        exportTableToCsv("users.csv", allUsers, [
            { header: "Name", value: (user) => [user.firstName, user.lastName].filter(Boolean).join(" ") || "-" },
            { header: "Mobile", value: (user) => user.mobile || "-" },
            { header: "Linked Vehicles", value: vehicles },
            { header: "Status", value: (user) => accountStatus(user).label },
            { header: "Registered Date", value: (user) => (user.time ? formatDate(user.time) : "-") },
            { header: "Device Type", value: (user) => user.deviceType || "-" },
        ]);
    };
 
    const footerContent = (
        <div>
            <Button label="No" icon="pi pi-times" onClick={() => setVisible(false)} />
            <Button
                label="Yes"
                icon="pi pi-check"
                onClick={() => {
                    setVisible(false);
                    deleteUser(selectedRow?.[0]?._id);
                }}
                autoFocus
            />
        </div>
    );
 
    return (
        <>
            <Dialog
                dismissableMask={true}
                visible={visible}
                showHeader={false}
                draggable={false}
                closable={false}
                style={{ width: "40vw" }}
                onHide={() => {
                    if (!visible) return;
                    setVisible(false);
                }}
                footer={
                    dialogType === "delete" ? (
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setVisible(false)} />
                            {canDeleteUsers && (
                                <>
                                    <Button
                                        label="Deactivate"
                                        icon="pi pi-pause"
                                        severity="warning"
                                        onClick={handleSoftDeleteUser}
                                        autoFocus
                                    />
                                    <Button
                                        label="Delete permanently"
                                        icon="pi pi-trash"
                                        severity="danger"
                                        onClick={handlePermanentDeleteUser}
                                    />
                                </>
                            )}
                        </div>
                    ) : dialogType === "restore" ? (
                        <div className="flex justify-content-end gap-2">
                            <Button label="Cancel" icon="pi pi-times" outlined onClick={() => setVisible(false)} />
                            {canDeleteUsers && (
                                <Button label="Restore" icon="pi pi-check" onClick={handleRestoreUser} autoFocus />
                            )}
                        </div>
                    ) : (
                        <div>
                            <Button label="Cancel" icon="pi pi-times" onClick={() => setVisible(false)} />
                            <Button label="Save" icon="pi pi-check" onClick={handleSavePlan} autoFocus />
                        </div>
                    )
                }
            >
                {dialogType === "delete" ? (
                    <>
                        <h5 className="mt-5 font-bold">How do you want to delete this user?</h5>
                        <p className="mt-3">User Id: {selectedRow?.[0]?.userId}</p>
                        <div className="mt-4 flex flex-column gap-3">
                            <div>
                                <span className="font-bold">Deactivate (temporary)</span>
                                <p className="mt-1 mb-0">
                                    The account and its data are hidden. Vehicles stay linked and nothing is
                                    lost. The user cannot log back in — only you can restore the account from
                                    this page.
                                </p>
                            </div>
                            <div>
                                <span className="font-bold text-red-500">Delete permanently</span>
                                <p className="mt-1 mb-0">
                                    Cannot be undone. The user is removed, their vehicles are unlinked and any
                                    QR codes on those vehicles are deactivated.
                                </p>
                            </div>
                        </div>
                    </>
                ) : dialogType === "restore" ? (
                    <>
                        <h5 className="mt-5 font-bold">Restore this account?</h5>
                        <p className="mt-3">User Id: {selectedRow?.[0]?.userId}</p>
                        <p className="mt-2 mb-0">
                            The account, its vehicles and any still-valid subscription come back exactly as
                            they were before deactivation.
                        </p>
                    </>
                ) : (
                    <>
                        <h5 className="mt-3 font-bold">Purchase Plan for User</h5>
                        <div className="flex flex-column gap-3 mt-4">
                            {/* Select Plan Type */}
                            <Dropdown
                                value={planType}
                                options={planTypes}
                                onChange={(e) => setPlanType(e.value)}
                                placeholder="Select Plan Type"
                                className="w-full"
                            />
 
                            {/* Select Plan */}
                            <Dropdown
                                value={selectedPlan}
                                options={allPlans}
                                onChange={(e) => {
                                    setSelectedPlan(e.value);
                                    console.log("e.value-->", e.value);
 
                                    // const plan = allPlans.find((p) => p._id === e.value._id);
                                    // console.log("plan-->",plan);
 
                                    // if (plan) {
                                    //     const newEndDate = new Date();
                                    //     newEndDate.setDate(newEndDate.getDate() + plan.duration);
                                    //     setEndDate(newEndDate);
                                    // }
                                }}
                                placeholder="Select a Plan"
                                className="w-full"
                                disabled={!planType}
                                optionLabel="name"
                            />
 
                            {/* Start & End Dates */}
                            {/* <div className="flex gap-3">
                                <Calendar value={startDate} onChange={(e) => setStartDate(e.value)} dateFormat="mm/dd/yy" showIcon />
                                <Calendar value={endDate} onChange={(e) => setEndDate(e.value)} dateFormat="mm/dd/yy" showIcon />
                            </div> */}
                        </div>
                    </>
                )}
            </Dialog>
 
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between mb-4">
                        <h5>{heading}</h5>
                        <div className="flex align-items-center">
                            {setSearch ? (
                                <div className="p-input-icon-left mx-2">
                                    <i className="pi pi-search" />
                                    <InputText
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder="Name, mobile, email or vehicle number"
                                    />
                                </div>
                            ) : null}
                            {setStatus ? (
                                <Dropdown
                                    className="mx-2"
                                    value={status}
                                    options={statusOptions}
                                    onChange={(e) => setStatus(e.value)}
                                    placeholder="Status"
                                />
                            ) : null}
                            <Button
                                label="Export CSV"
                                icon="pi pi-download"
                                onClick={exportAllUsers}
                                disabled={!allUsers.length}
                                severity="success"
                            />
                        </div>
                    </div>
                    <DataTable
                        paginator
                        value={allUsers}
                        first={first}
                        rows={rows}
                        onPage={onPage}
                        selection={selectedRow}
                        onSelectionChange={(e) => setSelectedRow(e.value)}
                        selectionMode="checkbox"
                        filter={filters}
                        filterDisplay="menu"
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        sortMode="multiple"
                        loading={loading}
                        emptyMessage="No user found."
                        className="custom-datatable-header"
                        showGridlines
                    >
                        {/* <Column field="userId" header="User ID"></Column> */}
                        <Column
                            body={(rowData) => [rowData.firstName, rowData.lastName].filter(Boolean).join(" ") || "-"}
                            header="Name"
                            sortable
                            sortField="firstName"
                        ></Column>
                        <Column field="mobile" header="Mobile" sortable></Column>
                        <Column body={vehicles} header="Linked Vehicles" sortable sortField={sortByLinkedVehicles}></Column>
                        <Column body={statusBody} header="Status" sortable sortField={(rowData) => accountStatus(rowData).label}></Column>
                        <Column
                            header="Registered Date"
                            field="time"
                            dataType="date"
                            filterMatchMode="dateIs"
                            showFilterMatchModes={true}
                            body={(rowData) => formatDate(rowData.time)}
                            filter
                            filterElement={dateFilterTemplate}
                            style={{ width: "150px" }}
                            sortable
                        />
                        <Column field="deviceType" header="Device Type" sortable></Column>
                        <Column body={image} header="Image" style={{ width: "80px" }}></Column>
                        <Column body={actions} style={{ width: "80px" }} header="Action"></Column>
                    </DataTable>
                </div>
            </div>
        </>
    );
}
